import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaceIcon from '@mui/icons-material/Place';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PublicIcon from '@mui/icons-material/Public';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate } from 'react-router-dom';
import type { Trip } from '../types';
import { TRIP_CATEGORIES } from '../types';

interface TripCardProps {
  trip: Trip;
  onEdit: (trip: Trip) => void;
  onDelete: (trip: Trip) => void;
}

function getPrivacyIcon(privacy: string) {
  switch (privacy) {
    case 'public':
      return <PublicIcon fontSize="small" />;
    case 'friends':
      return <GroupIcon fontSize="small" />;
    case 'private':
      return <LockIcon fontSize="small" />;
    default:
      return null;
  }
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function TripCard({ trip, onEdit, onDelete }: TripCardProps) {
  const navigate = useNavigate();
  const categoryLabel = TRIP_CATEGORIES.find((c) => c.value === trip.category)?.label || trip.category;

  return (
    <Card
      sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}
      onClick={() => navigate(`/trips/${trip.id}`)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="h2" noWrap sx={{ flex: 1 }}>
            {trip.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
            {getPrivacyIcon(trip.privacy)}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, color: 'text.secondary' }}>
          <CalendarTodayIcon fontSize="small" />
          <Typography variant="body2">
            {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
          </Typography>
        </Box>

        {trip.countryCode && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, color: 'text.secondary' }}>
            <PublicIcon fontSize="small" />
            <Typography variant="body2">{trip.countryCode}</Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Chip label={categoryLabel} size="small" variant="outlined" />
          {trip.pinCount !== undefined && (
            <Chip
              icon={<PlaceIcon />}
              label={`${trip.pinCount} pins`}
              size="small"
              variant="outlined"
              color="primary"
            />
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
        <IconButton
          size="small"
          aria-label="editar trip"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(trip);
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label="excluir trip"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(trip);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  );
}
